/**
 * Redaction of access-secret query parameters from URLs bound for analytics.
 *
 * Three distinct mechanisms in this codebase put a live access secret in the
 * address bar, and all three are reusable-or-single-use credentials rather than
 * identifiers:
 *
 * - `?invite=`     — `Space.invite_token`, the reusable per-space share link.
 *                    Anyone holding it can join an unlisted space.
 * - `?invitation=` — `SpaceInvitation.token`, the single-use per-email invite.
 * - `?token=`      — the generic spelling, reserved here so a future surface
 *                    cannot reintroduce the leak under a different name.
 * - `?email=`      — the magic-link routes' address param. Not a credential
 *                    like the three above, and it is here anyway.
 * - `?flow=`       — the magic-link flow-context id. Not an account credential
 *                    either, but a capability: it reads back the pending intent
 *                    and destination the link was minted for, and a
 *                    JOIN_COMMUNITY intent can carry a space invite token
 *                    inside it. Same reasoning as `token` below: the pre-router
 *                    strip is the real defence and it is allowed to fail, so
 *                    this list is what stands between a hardened in-app webview
 *                    and Amplitude.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the `token` entry was described as
 * "reserved here so a future surface cannot reintroduce the leak under a
 * different name", and `/auth/link?token=…&email=…` is that future surface
 * arriving under a different name. `bootstrapAuthLinkParams` strips both params
 * before Amplitude can observe the URL, and that is the real defence — but its
 * `history.replaceState` is wrapped in a deliberate swallow, because a throw at
 * module-evaluation time is a permanently blank page. The environments where it
 * throws are named in that catch: sandboxed iframes and hardened in-app webviews,
 * i.e. the mail-client viewers this feature's traffic goes through, plus Safari's
 * throttle past ~100 calls in 30s. On exactly those arrivals the params stay in
 * the URL, and this list is the only thing between them and Amplitude's Page
 * Location, Page URL, Element Href and session replay. The token was redacted;
 * the address was not. AuthLinkLanding's rule 2 says the address must never
 * reach analytics — "not the address, not even a masked address".
 *
 * The VALUE is replaced, never the key, because "did this visit carry an
 * invite" is load-bearing for attribution and k-factor measurement (see
 * docs/K_FACTOR_MEASUREMENT_STRATEGY.md). `?invite=redacted` keeps that signal
 * while destroying the credential.
 *
 * @see docs/onboarding/onboarding-design.md — the exposure this closes
 */

/** Replacement written in place of a secret's value. */
export const REDACTED_VALUE = 'redacted';

/**
 * Query parameter names whose values are access secrets. Matched
 * case-insensitively on the raw (still percent-encoded) key: every producer of a URL
 * THIS MODULE SEES writes these lowercase and unencoded, so decoding the key first
 * would buy nothing here and would make the match harder to reason about.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: that premise is about ANALYTICS URLs only, and it
 * is narrowed deliberately. The magic-link carrier introduced a channel with no such
 * producer -- `redirect_path` comes back over the network, authored by whoever
 * requested the link -- so a consumer that has to DECIDE something about a path
 * cannot rest on it: `?%69nvite=` is a real shape there, and
 * `@lib/magicLink/capabilityPaths` decodes the key for exactly that reason. Here the
 * cost of a miss is a secret in an Amplitude property rather than a capability
 * re-addressed, and the list is still the one source of truth for both.
 */
// AIDEV-NOTE: `key` is the live poll's presenter key (`/live/{code}/screen?key=`, TECH-867): held, it watches the
// big screen. Matched everywhere, not only under /live/: a network capture's query string has no path to scope by.
export const SECRET_QUERY_PARAMS = [
  'invite',
  'invitation',
  'token',
  'email',
  'flow',
  'key',
] as const;

const SECRET_QUERY_PARAM_SET: ReadonlySet<string> = new Set<string>(SECRET_QUERY_PARAMS);

/**
 * Query parameter names whose values are personal data typed by the user (a member or attendee
 * name search). Redacted only from network-capture query strings ({@link redactQuerySecrets});
 * page URLs keep them, and they are NOT access secrets, so `capabilityPaths` and replay ignore them.
 */
export const PII_QUERY_PARAMS = ['search'] as const;

const QUERY_STRING_REDACT_SET: ReadonlySet<string> = new Set<string>([
  ...SECRET_QUERY_PARAMS,
  ...PII_QUERY_PARAMS,
]);

/**
 * A live path inside a query VALUE, plain or percent-encoded (`/live/{code}`, `%2Flive%2F{code}`, also under
 * `/api/v1/`): the code is a credential (TECH-867), so `returnTo=/live/{code}/host` must not carry it.
 */
const LIVE_CODE_IN_VALUE = /((?:\/|%2F)live(?:\/|%2F))([A-Za-z0-9_-]+)/gi;

const redactLiveCodesInValue = (value: string): string =>
  value.replace(LIVE_CODE_IN_VALUE, (whole, prefix: string, code: string) =>
    code === 'code' ? whole : `${prefix}[code]`,
  );

/**
 * Rewrite the secret-bearing segments of one `key=value&...` string.
 *
 * Operates on raw segments rather than `URLSearchParams` on purpose:
 * `URLSearchParams.toString()` re-encodes everything it round-trips (spaces
 * become `+`, reserved characters shift), which would silently rewrite the
 * `source`, `tab` and `event` parameters that carry real analytics value. Only
 * the matched segment is touched here; every other byte is passed through.
 */
const redactQuerySegments = (
  query: string,
  paramSet: ReadonlySet<string> = SECRET_QUERY_PARAM_SET,
): { query: string; changed: boolean } => {
  let changed = false;

  const redacted = query
    .split('&')
    .map(segment => {
      const separator = segment.indexOf('=');
      // A valueless param (`?invite`) carries no secret.
      if (separator === -1) {
        return segment;
      }

      const key = segment.slice(0, separator);
      const value = segment.slice(separator + 1);

      // An empty value (`?invite=`) carries no secret either. Leaving it alone
      // avoids fabricating an "invite was present" signal where there was none.
      if (value === '' || !paramSet.has(key.toLowerCase())) {
        // Any other value may carry a live path (`returnTo=/live/{code}/host`): its code goes.
        const scrubbed = redactLiveCodesInValue(value);
        if (scrubbed === value) return segment;
        changed = true;
        return `${key}=${scrubbed}`;
      }

      changed = true;
      return `${key}=${REDACTED_VALUE}`;
    })
    .join('&');

  return { query: redacted, changed };
};

/**
 * Replace the value of every access-secret (and PII search) parameter in a BARE query string —
 * `key=value&key=value`, with no leading `?` and no URL around it. A leading
 * `?` folds into the first key (`?invite` !== `invite`), so that parameter's
 * secret silently survives redaction — a caller error, not handled here.
 *
 * Separate entry point rather than a shape guess inside {@link redactUrlSecrets},
 * which returns its input untouched the moment there is no `?` to find. The SDK's
 * network-capture plugin hands out exactly this shape: `[Amplitude] URL Query` is
 * built as `new URL(requestUrl).searchParams.toString()`, so the invite token on
 * `GET /api/v1/spaces/{id}?token=…` arrives here as a bare `token=<secret>`.
 *
 * Returns the input string unchanged (identical value) when nothing matches, on
 * the same contract as {@link redactUrlSecrets}.
 */
export const redactQuerySecrets = (query: string): string => {
  if (typeof query !== 'string' || query === '') {
    return query;
  }

  const result = redactQuerySegments(query, QUERY_STRING_REDACT_SET);
  return result.changed ? result.query : query;
};

/**
 * Replace the value of every access-secret query parameter in `url` with
 * {@link REDACTED_VALUE}, preserving everything else byte for byte.
 *
 * Returns the input string unchanged (identical value, not a rebuilt copy) when
 * there is nothing to redact, so callers can use referential equality to detect
 * a no-op.
 *
 * Both the query string and a query-shaped fragment are scrubbed, so a hash
 * route (`#/space?invite=…`) is covered as well as a normal one. A fragment
 * with no `?` is left alone: OIDC implicit-flow fragments (`#access_token=…`)
 * are a different subsystem's secrets and are not in scope here.
 */
export const redactUrlSecrets = (input: string): string => {
  if (typeof input !== 'string' || input === '') {
    return input;
  }
  const url = redactPathSecrets(input);

  const hashIndex = url.indexOf('#');
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? '' : url.slice(hashIndex + 1);

  // Returns its own `changed` flag rather than setting an outer one: a closure
  // that mutates a captured `let` defeats TypeScript's control-flow narrowing,
  // which then reads the final check as always-true.
  const redactPart = (part: string): { value: string; changed: boolean } => {
    const queryIndex = part.indexOf('?');
    if (queryIndex === -1) {
      return { value: part, changed: false };
    }

    const result = redactQuerySegments(part.slice(queryIndex + 1));
    if (!result.changed) {
      return { value: part, changed: false };
    }

    return { value: `${part.slice(0, queryIndex + 1)}${result.query}`, changed: true };
  };

  const head = redactPart(beforeHash);
  const tail = hashIndex === -1 ? { value: '', changed: false } : redactPart(fragment);

  if (!head.changed && !tail.changed) {
    return url;
  }

  return hashIndex === -1 ? head.value : `${head.value}#${tail.value}`;
};

/** Written in place of a live poll's code in a path (`/live/[code]/screen`). */
export const REDACTED_LIVE_CODE = '[code]';

/**
 * The live code's path segment: `/live/{code}` and `/api/v1/live/{code}/…`, in a bare path or after an origin.
 * Anchored at the path's start, so `/communities/live` or a `/live` list read stay as they are.
 */
const LIVE_CODE_PATH = /^((?:[a-z][a-z0-9+.-]*:)?\/\/[^/?#]*)?((?:\/api\/v1)?\/live\/)([^/?#]+)/i;

/**
 * Replace a live poll's code in a URL's or a path's PATH with {@link REDACTED_LIVE_CODE}.
 *
 * AIDEV-NOTE: the live code is a credential, not an id (TECH-867 FE-L3a review): it stands in for an unlisted
 * join Space's invite token (CONTRACT § 9.2). The SDK builds `[Amplitude] Page URL` and `Page Path` query-free,
 * so the query redaction above never reached it. Returns the input unchanged (same string) when it holds none.
 */
export const redactPathSecrets = (value: string): string => {
  if (typeof value !== 'string' || value === '') {
    return value;
  }
  const match = LIVE_CODE_PATH.exec(value);
  if (!match || match[3] === REDACTED_LIVE_CODE) {
    return value;
  }
  return `${(match[1] as string | undefined) ?? ''}${match[2]}${REDACTED_LIVE_CODE}${value.slice(match[0].length)}`;
};

/**
 * URL written into Session Replay in place of any secret-bearing URL.
 *
 * Session Replay's filter cannot do partial rewrites — see
 * {@link sessionReplayUgcFilterRules} — so the whole URL is replaced. `.invalid`
 * is the RFC 2606 reserved TLD, which makes it unmistakable that this is a
 * redaction marker and not a page anyone visited.
 */
export const SESSION_REPLAY_REDACTED_URL = `https://redacted.invalid/?invite=${REDACTED_VALUE}`;

/** Session Replay's stand-in for any page under `/live/` (the live code is in the path). */
export const SESSION_REPLAY_REDACTED_LIVE_URL = `https://redacted.invalid/live/${REDACTED_VALUE}`;

/** A Session Replay UGC filter rule: `selector` is a URL glob, `replacement` is literal. */
export interface UgcFilterRule {
  selector: string;
  replacement: string;
}

/**
 * Filter rules that stop Session Replay from recording a secret-bearing URL.
 *
 * Two constraints from the installed SDK shape these rules:
 *
 * 1. **The whole URL is replaced, not just the parameter.** The SDK compiles
 *    each `selector` glob to an anchored regex whose `*` becomes a
 *    non-capturing `.*`, then runs `url.replace(regex, replacement)`. With no
 *    capture groups there is no backreference to rebuild the path from, so the
 *    path is lost in the replay. The equivalent event properties
 *    (`[Amplitude] Page Path`, `[Amplitude] Page URL`) are already query-free
 *    and keep carrying it.
 * 2. **Selectors must be static and scheme-prefixed.** The SDK validates every
 *    selector against `/^\/|^https?:\/\/[^\s]+$/` and THROWS on a failure,
 *    which would break Session Replay initialisation. Deriving a selector from
 *    `window.location.origin` would therefore crash the SDK on iOS, where
 *    Capacitor serves the app from `capacitor://localhost`. Hence the fixed
 *    `https://` / `http://` pair rather than an origin-derived rule.
 */
export const sessionReplayUgcFilterRules = (): UgcFilterRule[] => [
  ...SECRET_QUERY_PARAMS.flatMap(param => [
    { selector: `https://*${param}=*`, replacement: SESSION_REPLAY_REDACTED_URL },
    { selector: `http://*${param}=*`, replacement: SESSION_REPLAY_REDACTED_URL },
  ]),
  // The live code sits in the PATH (`/live/{code}`), and a glob cannot rewrite one segment: the whole URL goes.
  { selector: 'https://*/live/*', replacement: SESSION_REPLAY_REDACTED_LIVE_URL },
  { selector: 'http://*/live/*', replacement: SESSION_REPLAY_REDACTED_LIVE_URL },
  // An encoded live path in a query value (`returnTo=%2Flive%2F{code}%2Fhost`).
  { selector: 'https://*%2Flive%2F*', replacement: SESSION_REPLAY_REDACTED_LIVE_URL },
  { selector: 'http://*%2Flive%2F*', replacement: SESSION_REPLAY_REDACTED_LIVE_URL },
];
